import { z } from 'zod';
import { idSchema, isoDateSchema } from './schemas/common.ts';

export const proposedInboxMoveSchema = z
  .object({
    kind: z.literal('reschedule'),
    scheduledDate: isoDateSchema,
    startTimeLocal: z.string().regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/),
    summary: z
      .string()
      .trim()
      .min(1)
      .max(500)
      .transform((value) => value.replace(/[\u2013\u2014]/g, '-')),
  })
  .strict();
export type ProposedInboxMove = z.infer<typeof proposedInboxMoveSchema>;

export const inboxActionKindSchema = z.enum(['reschedule', 'visit_instruction', 'create_quote']);
export type InboxActionKind = z.infer<typeof inboxActionKindSchema>;
const reviewedText = (maximum: number) =>
  z
    .string()
    .trim()
    .min(1)
    .max(maximum)
    .transform((value) => value.replace(/[\u2013\u2014]/g, '-'));
export const proposedVisitInstructionSchema = z
  .object({ kind: z.literal('visit_instruction'), text: reviewedText(5000), summary: reviewedText(500) })
  .strict();
export const proposedInboxQuoteSchema = z
  .object({ kind: z.literal('create_quote'), internalNotes: reviewedText(8000), summary: reviewedText(500) })
  .strict();
export const proposedInboxActionSchema = z.discriminatedUnion('kind', [
  proposedInboxMoveSchema,
  proposedVisitInstructionSchema,
  proposedInboxQuoteSchema,
]);

export const inboxSuggestionIdSchema = z.uuid();

export const inboxActionTargetSchema = z
  .object({
    customerId: idSchema,
    customerName: z.string().min(1).max(500),
    scheduledDate: isoDateSchema,
    startTimeLocal: z.string(),
    timezone: z.string(),
  })
  .strict();

export const suggestInboxActionSchema = z
  .object({
    id: inboxSuggestionIdSchema,
    appointmentId: idSchema,
    sourceMessageId: idSchema,
  })
  .strict();
export const decideInboxActionSchema = z
  .object({
    decision: z.enum(['approve', 'dismiss']),
    token: z.string().max(4096).optional(),
  })
  .strict();

/** Additive contracts for separately gated action kinds. Reschedule remains the default. */
export const inboxActionKindQuerySchema = z.object({ kind: inboxActionKindSchema.default('reschedule') }).strict();
export const suggestInboxActionAnySchema = z.union([
  suggestInboxActionSchema.extend({ kind: z.literal('reschedule').optional() }).strict(),
  suggestInboxActionSchema.extend({ kind: z.literal('visit_instruction') }).strict(),
  z.object({ id: inboxSuggestionIdSchema, sourceMessageId: idSchema, kind: z.literal('create_quote') }).strict(),
]);
export const decideInboxActionAnySchema = decideInboxActionSchema
  .extend({ editedText: reviewedText(5000).optional(), editedNotes: reviewedText(8000).optional() })
  .strict();
export const inboxActionReceiptSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('visit_instruction'),
      instructionId: idSchema,
      appointmentId: idSchema,
      version: z.number().int().positive(),
    })
    .strict(),
  z.object({ kind: z.literal('create_quote'), quoteId: idSchema }).strict(),
]);
export type InboxActionReceipt = z.infer<typeof inboxActionReceiptSchema>;
export const inboxQuoteTargetSchema = inboxActionTargetSchema
  .extend({ scheduledDate: z.null(), startTimeLocal: z.null() })
  .strict();

export interface InboxActionSuggestion {
  id: string;
  appointmentId: string;
  proposal: ProposedInboxMove;
  target: z.infer<typeof inboxActionTargetSchema>;
  status: 'pending' | 'approved' | 'dismissed';
  expiresAt: string;
  createdAt: string;
}

export type InboxActionSuggestionAny =
  | InboxActionSuggestion
  | (Omit<InboxActionSuggestion, 'proposal'> & {
      proposal: z.infer<typeof proposedVisitInstructionSchema>;
      receipt?: Extract<InboxActionReceipt, { kind: 'visit_instruction' }>;
    })
  | (Omit<InboxActionSuggestion, 'appointmentId' | 'proposal' | 'target'> & {
      appointmentId: null;
      proposal: z.infer<typeof proposedInboxQuoteSchema>;
      target: z.infer<typeof inboxQuoteTargetSchema>;
      receipt?: Extract<InboxActionReceipt, { kind: 'create_quote' }>;
    });
